import {
  GetLinksResponse,
  SearchResult,
  PerplexityChatCompletionResponse,
} from '@lessonPlanner/commons';

export type SearchProvider = 'tavily' | 'serper' | 'perplexity';

export interface UnifiedSearchParams {
  query: string | string[];
  provider?: SearchProvider;
  stream?: boolean;
}

export type UnifiedSearchResult =
  | GetLinksResponse[]
  | SearchResult[]
  | PerplexityChatCompletionResponse;

export const performUnifiedSearch = async (
  params: UnifiedSearchParams,
): Promise<UnifiedSearchResult | ReadableStream<Uint8Array>> => {
  const response = await fetch('/api/search', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    const errorData = await response.json();
    console.error('Unified Search Error:', errorData);
    throw new Error(`Failed to perform search: ${JSON.stringify(errorData)}`);
  }

  // If streaming, return the response body as ReadableStream
  if (params.stream) {
    return response.body as ReadableStream<Uint8Array>;
  }

  // For non-streaming, parse as JSON
  return response.json() as Promise<UnifiedSearchResult>;
};

export const searchWithTavily = async (
  queries: string[],
): Promise<GetLinksResponse[]> => {
  const result = await performUnifiedSearch({
    query: queries,
    provider: 'tavily',
  });
  return result as GetLinksResponse[];
};

export const searchWithSerper = async (
  query: string,
): Promise<SearchResult[]> => {
  const result = await performUnifiedSearch({
    query,
    provider: 'serper',
  });
  return result as SearchResult[];
};

export const searchWithPerplexity = async (
  query: string,
  stream = false,
): Promise<PerplexityChatCompletionResponse | ReadableStream<Uint8Array>> => {
  const result = await performUnifiedSearch({
    query,
    provider: 'perplexity',
    stream,
  });

  return result as
    | PerplexityChatCompletionResponse
    | ReadableStream<Uint8Array>;
};
